/**
 * Packet Template Editor — view and edit the 19 parent packet form templates.
 * Admin/superadmin only. Route: /admin/packet-templates.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HistoryIcon from '@mui/icons-material/History';
import AppHeader from './AppHeader';
import { formsAPI } from '../services/api';

const PREVIEW_STUDENT_ID_KEY = 'packet_template_preview_student_id';

/** Student primary key only (matches Django Student.pk). Rejects placeholders like "test". */
function isValidPreviewStudentPk(raw) {
  const s = (raw ?? '').trim();
  if (!s || !/^\d+$/.test(s)) return false;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 && n <= Number.MAX_SAFE_INTEGER;
}

// Content key -> human label for each form (matches backend content schema)
const CONTENT_LABELS = {
  form_01: { date: 'Date', signatory_name: 'Signatory Name', signatory_title: 'Signatory Title' },
  form_02: { signatory_name: 'Signatory Name', signatory_title: 'Signatory Title' },
  form_03: { open_school_night_date: 'Open School Night Date', parent_teacher_conference_date: 'Parent Teacher Conference Date' },
  form_04: { signatory_name: 'Signatory Name', signatory_title: 'Signatory Title' },
  form_05: { calendar_title: 'Calendar Title' },
  form_07: { authorization_text: 'Authorization Text', notes: 'Notes' },
  form_08: { notes: 'Notes' },
  form_09: { notes: 'Notes' },
  form_10: { photo_instruction: 'Photo Instruction' },
  form_11: { notes: 'Notes' },
  form_12: { notes: 'Notes' },
  form_13: { notes: 'Notes' },
  form_14: { notes: 'Notes' },
  form_15: { release_para1: 'Release Paragraph 1', release_para2: 'Release Paragraph 2', notes: 'Notes' },
  form_16: { notes: 'Notes' },
  form_17: { application_year: 'Application Year', school_name: 'School Name', school_address: 'School Address', meals_contact_name: 'Meals Contact Name', school_phone: 'School Phone', note: 'Generated single-page meals application' },
  form_18: { nurse_1_name: 'Nurse 1 Name', nurse_2_name: 'Nurse 2 Name', nurse_ext_1: 'Nurse Ext 1', nurse_3_name: 'Nurse 3 Name', nurse_ext_2: 'Nurse Ext 2', phone: 'Phone Number' },
  form_19: {
    initial_line_1: 'Initial Line 1',
    initial_line_2: 'Initial Line 2',
    initial_line_3: 'Initial Line 3',
    notes: 'Notes',
  },
};

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/** Shallow string diff for template `content` JSON (string values). */
function diffTemplateContent(leftObj, rightObj) {
  const L = leftObj && typeof leftObj === 'object' ? leftObj : {};
  const R = rightObj && typeof rightObj === 'object' ? rightObj : {};
  const keys = new Set([...Object.keys(L), ...Object.keys(R)]);
  const rows = [];
  keys.forEach((k) => {
    const a = L[k];
    const b = R[k];
    if (a === b) return;
    if (a === undefined) rows.push({ key: k, kind: 'added', left: '', right: String(b ?? '') });
    else if (b === undefined) rows.push({ key: k, kind: 'removed', left: String(a ?? ''), right: '' });
    else rows.push({ key: k, kind: 'changed', left: String(a ?? ''), right: String(b ?? '') });
  });
  rows.sort((x, y) => x.key.localeCompare(y.key));
  return rows;
}

export default function PacketTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [content, setContent] = useState({});
  const [previewStudentId, setPreviewStudentId] = useState(() => {
    const stored = localStorage.getItem(PREVIEW_STUDENT_ID_KEY) || '';
    return isValidPreviewStudentPk(stored) ? stored.trim() : '';
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreviewSetting, setShowPreviewSetting] = useState(false);
  const [templateVersions, setTemplateVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffTitle, setDiffTitle] = useState('');
  const [diffRows, setDiffRows] = useState([]);
  const [diffLoading, setDiffLoading] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareLeftId, setCompareLeftId] = useState('');
  const [compareRightId, setCompareRightId] = useState('');
  const [revertLoading, setRevertLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    setError('');
    try {
      const res = await formsAPI.getTemplates();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
      setTemplates(data);
      if (data?.length) {
        setSelectedFormId((prev) => prev || data[0].form_id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!selectedFormId) return;
    setError('');
    setSelectedTemplate(null);
    formsAPI.getTemplate(selectedFormId)
      .then((res) => {
        setSelectedTemplate(res.data);
        setContent(res.data?.content ? { ...res.data.content } : {});
      })
      .catch((e) => setError(e.response?.data?.detail || e.message || 'Failed to load template'));
  }, [selectedFormId]);

  const reloadVersions = useCallback(() => {
    if (!selectedFormId) return;
    setVersionsLoading(true);
    return formsAPI
      .getTemplateVersions(selectedFormId)
      .then((res) => setTemplateVersions(res.data?.versions || []))
      .catch(() => setTemplateVersions([]))
      .finally(() => setVersionsLoading(false));
  }, [selectedFormId]);

  useEffect(() => {
    reloadVersions();
  }, [reloadVersions]);

  const openDiff = (title, leftContent, rightContent) => {
    setDiffTitle(title);
    setDiffRows(diffTemplateContent(leftContent, rightContent));
    setDiffOpen(true);
  };

  const handleDiffVersionVsSaved = async (v) => {
    if (!selectedFormId) return;
    setDiffLoading(true);
    setError('');
    try {
      const res = await formsAPI.getTemplateVersion(selectedFormId, v.id);
      const saved = selectedTemplate?.content || {};
      openDiff(
        `Version ${v.version_number} (left) vs saved template (right)`,
        res.data?.content || {},
        saved,
      );
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load version');
    } finally {
      setDiffLoading(false);
    }
  };

  const handleOpenCompareDialog = () => {
    const list = templateVersions;
    if (list.length < 2) {
      setError('Need at least two versions to compare.');
      return;
    }
    setCompareLeftId(String(list[1].id));
    setCompareRightId(String(list[0].id));
    setCompareOpen(true);
  };

  const handleRunCompare = async () => {
    if (!selectedFormId || !compareLeftId || !compareRightId) return;
    if (compareLeftId === compareRightId) {
      setError('Pick two different versions.');
      return;
    }
    setDiffLoading(true);
    setError('');
    try {
      const [a, b] = await Promise.all([
        formsAPI.getTemplateVersion(selectedFormId, compareLeftId),
        formsAPI.getTemplateVersion(selectedFormId, compareRightId),
      ]);
      const vnA = a.data?.version_number ?? '?';
      const vnB = b.data?.version_number ?? '?';
      openDiff(`Version ${vnA} vs version ${vnB}`, a.data?.content || {}, b.data?.content || {});
      setCompareOpen(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Compare failed');
    } finally {
      setDiffLoading(false);
    }
  };

  const handleRevertToVersion = async (v) => {
    if (!selectedFormId) return;
    const ok = window.confirm(
      `Revert template to version ${v.version_number}? This updates the live template and creates a new audit version.`,
    );
    if (!ok) return;
    setRevertLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await formsAPI.revertTemplateToVersion(selectedFormId, v.id);
      setSelectedTemplate(res.data);
      setContent(res.data?.content ? { ...res.data.content } : {});
      setSuccess(`Reverted to version ${v.version_number}.`);
      setTimeout(() => setSuccess(''), 4000);
      await reloadVersions();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Revert failed');
    } finally {
      setRevertLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedFormId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await formsAPI.updateTemplate(selectedFormId, { content });
      setSelectedTemplate(res.data);
      setSuccess('Saved.');
      setTimeout(() => setSuccess(''), 3000);
      await reloadVersions();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    const sid = previewStudentId.trim();
    if (!sid) {
      setError('Set a preview student ID first (click the gear icon).');
      return;
    }
    if (!isValidPreviewStudentPk(sid)) {
      setError(
        'Preview student ID must be the numeric primary key from the student record (e.g. URL /students/123 → 123). '
        + 'Non-numeric values such as "test" are rejected and will not call the preview API.',
      );
      return;
    }
    setError('');
    try {
      const res = await formsAPI.previewForm({ student_id: sid, form_id: selectedFormId });
      const blob = res.data;
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Preview failed. Check preview student ID and try again.');
    }
  };

  const savePreviewStudentId = () => {
    const sid = previewStudentId.trim();
    if (sid && !isValidPreviewStudentPk(sid)) {
      setError('Save a numeric student ID only, or clear the field. Non-numeric values are not stored.');
      return;
    }
    if (sid) localStorage.setItem(PREVIEW_STUDENT_ID_KEY, sid);
    else localStorage.removeItem(PREVIEW_STUDENT_ID_KEY);
    setShowPreviewSetting(false);
  };

  const labels = selectedFormId ? (CONTENT_LABELS[selectedFormId] || {}) : {};
  const contentKeys = Object.keys(labels);

  return (
    <>
      <AppHeader />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Packet Templates</Typography>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>Forms</Typography>
                <List dense disablePadding>
                  {templates.map((t) => (
                    <ListItemButton
                      key={t.form_id}
                      selected={selectedFormId === t.form_id}
                      onClick={() => setSelectedFormId(t.form_id)}
                    >
                      <ListItemText primary={`${t.form_id.replace('_', ' ')} — ${t.form_name}`} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {selectedTemplate ? (
                  <>
                    <Typography variant="h6">{selectedTemplate.form_name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {selectedTemplate.form_id}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Version history
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Each save snapshots content. Diff a revision against the saved template, compare any two versions, or revert (creates a new version).
                    </Typography>
                    {versionsLoading ? (
                      <CircularProgress size={22} sx={{ mb: 2 }} />
                    ) : templateVersions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No version history yet.</Typography>
                    ) : (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CompareArrowsIcon />}
                            onClick={handleOpenCompareDialog}
                            disabled={templateVersions.length < 2 || diffLoading}
                          >
                            Compare two versions
                          </Button>
                        </Box>
                        <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          {templateVersions.map((v) => (
                            <ListItem
                              key={v.id}
                              alignItems="flex-start"
                              divider
                              secondaryAction={(
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 220, justifyContent: 'flex-end' }}>
                                  <Button size="small" onClick={() => handleDiffVersionVsSaved(v)} disabled={diffLoading}>
                                    Diff vs saved
                                  </Button>
                                  <Button
                                    size="small"
                                    color="warning"
                                    startIcon={<HistoryIcon />}
                                    onClick={() => handleRevertToVersion(v)}
                                    disabled={revertLoading}
                                  >
                                    Revert
                                  </Button>
                                </Box>
                              )}
                            >
                              <ListItemText
                                primary={`v${v.version_number}`}
                                secondary={`${formatDateTime(v.created_at)}${v.notes ? ` — ${v.notes}` : ''}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {contentKeys.length === 0 ? (
                      <Typography color="text.secondary">No editable fields for this form.</Typography>
                    ) : (
                      <Box component="form" noValidate sx={{ mb: 2 }}>
                        {contentKeys.map((key) => (
                          <TextField
                            key={key}
                            fullWidth
                            multiline={key === 'release_para1' || key === 'release_para2' || key === 'confirmation_text' || key === 'checklist_intro' || key === 'photo_instruction' || key === 'authorization_text'}
                            rows={key === 'release_para1' || key === 'release_para2' || key === 'confirmation_text' ? 3 : key === 'checklist_intro' || key === 'photo_instruction' || key === 'authorization_text' ? 2 : 1}
                            label={labels[key]}
                            value={content[key] ?? ''}
                            onChange={(e) => setContent((prev) => ({ ...prev, [key]: e.target.value }))}
                            margin="normal"
                            size="small"
                          />
                        ))}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Saving…' : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={handlePreview}
                        disabled={!selectedFormId}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => setShowPreviewSetting((s) => !s)}
                      >
                        Preview student ID
                      </Button>
                    </Box>

                    {showPreviewSetting && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          label="Student ID for preview (numeric PK)"
                          value={previewStudentId}
                          onChange={(e) => setPreviewStudentId(e.target.value)}
                          onBlur={savePreviewStudentId}
                          placeholder="e.g. 123 from /students/123"
                          sx={{ width: 180 }}
                        />
                        <Button size="small" onClick={savePreviewStudentId}>Save</Button>
                      </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {selectedTemplate.last_edited_by
                        ? `Last edited by ${selectedTemplate.last_edited_by} on ${formatDateTime(selectedTemplate.last_edited_at)}`
                        : `Last edited: ${formatDateTime(selectedTemplate.last_edited_at)}`}
                    </Typography>
                  </>
                ) : (
                  <Typography color="text.secondary">Select a form from the list.</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>

      <Dialog open={compareOpen} onClose={() => !diffLoading && setCompareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Compare two versions</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Left (older side)</InputLabel>
            <Select
              label="Left (older side)"
              value={compareLeftId}
              onChange={(e) => setCompareLeftId(e.target.value)}
            >
              {templateVersions.map((v) => (
                <MenuItem key={`L-${v.id}`} value={String(v.id)}>v{v.version_number} — {formatDateTime(v.created_at)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Right (newer side)</InputLabel>
            <Select
              label="Right (newer side)"
              value={compareRightId}
              onChange={(e) => setCompareRightId(e.target.value)}
            >
              {templateVersions.map((v) => (
                <MenuItem key={`R-${v.id}`} value={String(v.id)}>v{v.version_number} — {formatDateTime(v.created_at)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareOpen(false)} disabled={diffLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleRunCompare} disabled={diffLoading}>
            {diffLoading ? 'Loading…' : 'Show diff'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={diffOpen} onClose={() => setDiffOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{diffTitle}</DialogTitle>
        <DialogContent dividers>
          {diffRows.length === 0 ? (
            <Typography color="text.secondary">No differences in tracked fields (or identical content).</Typography>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Left (older / first)</TableCell>
                  <TableCell>Right (newer / second)</TableCell>
                  <TableCell width={100}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {diffRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.key}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 240, fontSize: 12 }}>
                      {row.left || '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 240, fontSize: 12 }}>
                      {row.right || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.kind}
                        color={row.kind === 'added' ? 'success' : row.kind === 'removed' ? 'error' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
